import { createFileRoute } from "@tanstack/react-router";
import { useNavigate } from "@tanstack/react-router";
import { useContext, useMemo } from "react";
import { useEffect } from "react";
import { ConfigContext, OrderDataContext, ReturnOrderContext } from "@/Layout";
import { useUpsellProducts } from "@/api/upsellProducts";
import { LineItem } from "@/api/verify";
import NextButton from "@/components/NextButton/NextButton";
import { ProductChoice } from "@/components/ProductChoice/ProductChoice.tsx";
import { ReturnContainer } from "@/components/ReturnContainer/ReturnContainer";
import { ReturnOrder } from "@/types";

export const Route = createFileRoute("/products")({
  component: ProductPage,
});

function ProductPage() {
  const { orderData } = useContext(OrderDataContext),
    { returnOrder, setReturnOrder } = useContext(ReturnOrderContext),
    configData = useContext(ConfigContext),
    navigate = useNavigate();

  const products = useMemo(() => orderData?.line_items ?? [], [orderData]);

  // cache upsell products early, so it's ready for the product explorer
  const shouldFetchUpsellProducts =
    orderData?.exchange_data?.upsell_enabled ?? false;
  useUpsellProducts("", {
    enabled: shouldFetchUpsellProducts,
  });

  useEffect(() => {
    if (!orderData || !configData) {
      navigate({
        to: "/",
        search: (prev) => prev,
      });
      return;
    }
  }, [orderData, configData, navigate]);

  useEffect(() => {
    if (!orderData) return;

    const autoCheckSingleItemOrder = () => {
      const newReturnOrder = returnOrder ?? ({} as ReturnOrder);
      if (!products[0].returnable || products.length > 1) return;
      setReturnOrder({
        ...newReturnOrder,
        order_name: orderData.order_name,
        email: orderData.email,
        shop: orderData.shop,
        portal_id: orderData.portal_id,
        line_items: [
          {
            id: products[0].id,
            line_item_id: products[0].line_item_id,
          },
        ],
        reasons: orderData.return_reasons.map((reason) => reason.id),
      });
    };

    if (products.length === 1) autoCheckSingleItemOrder();
  }, [orderData, returnOrder, setReturnOrder, products]);

  if (!orderData || !configData) {
    navigate({
      to: "/",

      search: (prev) => prev,
    });
    return;
  }

  const toggleReturnItem = (product: LineItem) => {
    const addItem = !returnOrder?.line_items.some(
      (item) => item.id === product.id
    );

    setReturnOrder({
      order_name: orderData.order_name,
      email: orderData.email,
      shop: orderData.shop,
      portal_id: orderData.portal_id,
      line_items: addItem
        ? [
            ...(returnOrder?.line_items ?? []),
            { id: product.id, line_item_id: product.line_item_id },
          ]
        : (returnOrder?.line_items ?? []).filter(
            (item) => item.id !== product.id
          ),
      reasons: orderData.return_reasons.map((reason) => reason.id),
    });
  };

  const productsElements = (
    <div className="flex flex-col sm:min-w-[600px]">
      {products.map((product) => (
        <>
          <ProductChoice
            product={product}
            key={product.id}
            onCheckboxChange={() => toggleReturnItem(product)}
            checked={(returnOrder?.line_items ?? []).some(
              (item) => item.id === product.id
            )}
            notReturnableMessage={configData?.not_returnable_message}
          />
          <hr className="my-4" />
        </>
      ))}
    </div>
  );

  return (
    <ReturnContainer>
      <div className="h-full">
        <div>
          <h1 className="font-semibold text-lg">
            {configData?.choose_products_header}
          </h1>
        </div>
        <p className="text-sm min-h-12 mt-2">
          {configData?.choose_products_message}
        </p>
        <h2 className="text-base font-semibold mb-2">
          {configData?.items_on_order_text}
        </h2>
        <hr className="mt-2 mb-4" />
        {productsElements}
        <div className="mt-auto sm:mt-8 mb-4 pb-8 sm:pb-0">
          <NextButton
            onClick={() => {
              navigate({
                to: "/return-item/$id/return-reason",
                search: (prev) => prev,
                params: {
                  id: (returnOrder?.line_items ?? [])[0]?.id ?? "",
                },
              });
            }}
            text={configData?.choose_products_button_message}
            buttonColor={configData?.button_color}
            buttonDisabled={(returnOrder?.line_items ?? []).length === 0}
          />
        </div>
      </div>
    </ReturnContainer>
  );
}
